<script setup>
import { ref, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { fetchHomepageSection, updateHomepageSection, uploadHomeSectionFile } from "./api";

const route = useRoute(), router = useRouter();
const id = route.params.id;
const loading = ref(false), saving = ref(false), success = ref(false), error = ref(""), validationError = ref(""), uploading = ref(false), notFound = ref(false);
const form = ref({ name: "", isEnabled: true, isLink: false, redirectLink: "" });

onMounted(async () => {
  loading.value = true;
  try {
    const s = await fetchHomepageSection(id);
    if (!s) { notFound.value = true; return; }
    form.value = { name: s.name ?? "", isEnabled: s.isEnabled ?? true, isLink: s.isLink || false, redirectLink: s.redirectLink || "" };
  } catch { error.value = "Couldn't load the section."; }
  finally { loading.value = false; }
});

async function onFile(e) {
  const file = e.target.files?.[0]; e.target.value = "";
  if (!file) return;
  uploading.value = true;
  try { form.value.redirectLink = await uploadHomeSectionFile(file); }
  catch { error.value = "Couldn't upload the file."; }
  finally { uploading.value = false; }
}

async function save() {
  success.value = false; error.value = ""; validationError.value = "";
  if (!form.value.name.trim()) { validationError.value = "Name is required."; return; }
  saving.value = true;
  try { await updateHomepageSection(id, { ...form.value }); success.value = true; setTimeout(() => router.push({ name: "homepage-sections" }), 800); }
  catch { error.value = "Error occurred. Please try again."; }
  finally { saving.value = false; }
}
</script>

<template>
  <section>
    <VBtn variant="text" prepend-icon="mdi:arrow-left" class="mb-4" @click="router.push({ name: 'homepage-sections' })">Back</VBtn>
    <h1 class="text-h5 mb-4">Edit Homepage Section</h1>
    <p v-if="loading">Loading…</p>
    <VAlert v-else-if="notFound" type="error">Section not found.</VAlert>
    <VCard v-else class="pa-4" max-width="640">
      <VAlert v-if="error" type="error" class="mb-4">{{ error }}</VAlert>
      <VAlert v-if="success" type="success" class="mb-4">Updated successfully.</VAlert>
      <form @submit.prevent="save">
        <VTextField v-model="form.name" label="Section Name" class="mb-4" />
        <VSwitch v-model="form.isLink" label="Open Browser?" class="mb-2" />
        <p class="text-caption text-medium-emphasis mb-4">Note: when disabled the app opens the rewards redemption page.</p>
        <VTextField v-model="form.redirectLink" label="Redirect Link" class="mb-4" />
        <div class="mb-4">
          <div class="text-body-2 font-weight-semibold mb-1">Upload PDF / Image / Video (sets Redirect Link)</div>
          <input type="file" accept="image/*,application/pdf,video/*" @change="onFile" />
          <span v-if="uploading" class="text-caption text-medium-emphasis ml-2">Uploading…</span>
        </div>
        <VSwitch v-model="form.isEnabled" label="Enabled" class="mb-4" />
        <VAlert v-if="validationError" type="warning" class="mb-4">{{ validationError }}</VAlert>
        <VBtn type="submit" :loading="saving" :disabled="uploading">Save</VBtn>
      </form>
    </VCard>
  </section>
</template>
